import React, { useEffect } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import './NotificationDropdown.css';

const NotificationDropdown = ({ onClose }) => {
    const {
        notifications,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification
    } = useNotifications();
    const navigate = useNavigate();

    useEffect(() => {
        fetchNotifications({ limit: 10 });
    }, [fetchNotifications]);

    const handleNotificationClick = async (notification) => {
        // Mark as read
        if (!notification.isRead) {
            await markAsRead(notification._id);
        }

        // Navigate to action URL if available
        if (notification.actionUrl) {
            navigate(notification.actionUrl);
            onClose();
        }
    };

    const handleMarkAllRead = async () => {
        await markAllAsRead();
    };

    const handleDelete = async (e, notificationId) => {
        e.stopPropagation();
        await deleteNotification(notificationId);
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const notifDate = new Date(date);
        const seconds = Math.floor((now - notifDate) / 1000);

        if (seconds < 60) return 'Just now';
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
        if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
        return notifDate.toLocaleDateString();
    };

    const getPriorityClass = (priority) => {
        switch (priority) {
            case 'urgent':
                return 'priority-urgent';
            case 'high':
                return 'priority-high';
            case 'medium':
                return 'priority-medium';
            default:
                return 'priority-low';
        }
    };

    return (
        <div className="notification-dropdown">
            <div className="notification-header">
                <h3>Notifications</h3>
                {notifications.length > 0 && (
                    <button
                        className="mark-all-read-btn"
                        onClick={handleMarkAllRead}
                    >
                        Mark all read
                    </button>
                )}
            </div>

            <div className="notification-list">
                {loading ? (
                    <div className="notification-loading">
                        <div className="spinner"></div>
                        <p>Loading notifications...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="no-notifications">
                        <svg
                            className="no-notif-icon"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                            />
                        </svg>
                        <p>No notifications</p>
                    </div>
                ) : (
                    notifications.map((notification) => (
                        <div
                            key={notification._id}
                            className={`notification-item ${
                                !notification.isRead ? 'unread' : ''
                            } ${getPriorityClass(notification.priority)}`}
                            onClick={() => handleNotificationClick(notification)}
                        >
                            <div className="notification-content">
                                <div className="notification-title">
                                    {notification.title}
                                    {!notification.isRead && (
                                        <span className="unread-dot"></span>
                                    )}
                                </div>
                                <div className="notification-message">
                                    {notification.message}
                                </div>
                                <div className="notification-footer">
                                    <span className="notification-time">
                                        {formatTimeAgo(notification.createdAt)}
                                    </span>
                                    {notification.actionLabel && (
                                        <span className="notification-action">
                                            {notification.actionLabel}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                className="delete-notification-btn"
                                onClick={(e) => handleDelete(e, notification._id)}
                                aria-label="Delete notification"
                            >
                                ×
                            </button>
                        </div>
                    ))
                )}
            </div>

            {notifications.length > 0 && (
                <div className="notification-footer-link">
                    <button
                        onClick={() => {
                            navigate('/notifications');
                            onClose();
                        }}
                    >
                        View all notifications
                    </button>
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
